import { getUTCOffsetString, uaParse } from './utils.js';
const refKey = '$__or__initial_ref__$';
const distinctIdKey = '$__or__distinct_device_id__$';
const utmParamsKey = '$__or__utm_params__$';
const superPropKey = '$__or__super_properties__$';
const userIdKey = '$__or__user_id__$';
const win = 'window' in globalThis
    ? window
    : {
        navigator: { userAgent: '' },
        screen: {},
        document: {
            cookie: '',
        },
        location: { search: '' },
    };
const doc = 'document' in globalThis ? document : { referrer: '' };
const searchEngineList = [
    'google',
    'bing',
    'yahoo',
    'baidu',
    'yandex',
    'duckduckgo',
    'ecosia',
    'ask',
    'aol',
    'wolframalpha',
    'startpage',
    'swisscows',
    'qwant',
    'lycos',
    'dogpile',
    'info',
    'teoma',
    'webcrawler',
    'naver',
    'seznam',
    'perplexity',
];
export default class ConstantProperties {
    constructor(localStorage, sessionStorage) {
        this.localStorage = localStorage;
        this.sessionStorage = sessionStorage;
        this.user_id = null;
        this.setUserId = (user_id) => {
            this.user_id = user_id;
            this.sessionStorage.setItem(userIdKey, user_id ?? '');
        };
        this.resetUserId = (hard) => {
            this.user_id = null;
            if (hard) {
                this.deviceId = this.getDistinctDeviceId(true);
            }
        };
        this.getDistinctDeviceId = (force) => {
            const potentialStored = this.localStorage.getItem(distinctIdKey);
            if (potentialStored && !force) {
                return potentialStored;
            }
            else {
                const distinctId = `${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
                this.localStorage.setItem(distinctIdKey, distinctId);
                return distinctId;
            }
        };
        this.getReferrer = () => {
            const potentialStored = this.sessionStorage.getItem(refKey);
            if (potentialStored) {
                return potentialStored;
            }
            else {
                const ref = doc.referrer;
                this.sessionStorage.setItem(refKey, ref);
                return ref;
            }
        };
        this.parseUTM = () => {
            const potentialStored = this.sessionStorage.getItem(utmParamsKey);
            if (potentialStored) {
                const obj = JSON.parse(potentialStored);
                this.utmSource = obj.utm_source;
                this.utmMedium = obj.utm_medium;
                this.utmCampaign = obj.utm_campaign;
            }
            else {
                const searchParams = new URLSearchParams(win.location.search);
                this.utmSource = searchParams.get('utm_source') || null;
                this.utmMedium = searchParams.get('utm_medium') || null;
                this.utmCampaign = searchParams.get('utm_campaign') || null;
                const obj = {
                    utm_source: this.utmSource,
                    utm_medium: this.utmMedium,
                    utm_campaign: this.utmCampaign,
                };
                this.sessionStorage.setItem(utmParamsKey, JSON.stringify(obj));
            }
        };
        this.getSearchEngine = (ref) => {
            for (const searchEngine of searchEngineList) {
                if (ref.includes(searchEngine)) {
                    return searchEngine;
                }
            }
            return null;
        };
        this.getSuperProperties = () => {
            const potentialStored = this.localStorage.getItem(superPropKey);
            if (potentialStored) {
                return JSON.parse(potentialStored);
            }
            else {
                return {};
            }
        };
        this.saveSuperProperties = (props) => {
            this.localStorage.setItem(superPropKey, JSON.stringify(props));
        };
        this.clearSuperProperties = () => {
            this.localStorage.setItem(superPropKey, JSON.stringify({}));
        };
        const { width, height, browser, browserVersion, browserMajorVersion, os, osVersion, mobile } = uaParse(win, (refinedOsVersion) => {
            // Windows 10/11 are indistinguishable from the UA string; the async
            // high-entropy result lands after construction, so update it live here.
            // `all` is a getter, so later events pick up the corrected value.
            this.osVersion = refinedOsVersion;
        });
        const storedUserId = this.sessionStorage.getItem(userIdKey);
        if (storedUserId) {
            this.user_id = storedUserId;
        }
        this.os = os;
        this.osVersion = osVersion;
        this.browser = `${browser}`;
        this.browserVersion = `${browserVersion} (${browserMajorVersion})`;
        this.platform = mobile ? 'mobile' : 'desktop';
        this.screenHeight = height;
        this.screenWidth = width;
        this.initialReferrer = this.getReferrer();
        this.deviceId = this.getDistinctDeviceId();
        this.searchEngine = this.getSearchEngine(this.initialReferrer);
        this.parseUTM();
    }
    get all() {
        return {
            os: this.os,
            os_version: this.osVersion,
            browser: this.browser,
            browser_version: this.browserVersion,
            platform: this.platform,
            screen_height: this.screenHeight,
            screen_width: this.screenWidth,
            initial_referrer: this.initialReferrer,
            utm_source: this.utmSource,
            utm_medium: this.utmMedium,
            utm_campaign: this.utmCampaign,
            user_id: this.user_id,
            distinct_id: this.deviceId,
            sdk_edition: 'web',
            sdk_version: 'TRACKER_VERSION',
            timezone: getUTCOffsetString(),
            search_engine: this.searchEngine,
        };
    }
    get defaultPropertyKeys() {
        return Object.keys(this.all);
    }
    get distinctId() {
        return this.deviceId;
    }
}
